import { heroImages } from "@/mocks/editorialImages";

export default function Hero() {
  const goTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" className="relative overflow-hidden bg-background-50 pt-28">
      {/* decorative editorial shapes */}
      <div className="pointer-events-none absolute -left-24 top-40 h-72 w-72 rounded-full bg-accent-200/60 blur-3xl"></div>
      <div className="pointer-events-none absolute -right-20 top-64 h-80 w-80 rounded-full bg-primary-200/50 blur-3xl"></div>

      <div className="section-wrap relative grid grid-cols-1 items-center gap-10 py-14 lg:grid-cols-[1.15fr_1fr] lg:gap-16">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-sm font-medium text-primary-800">
            <i className="ri-goblet-fill"></i>
            吃瓜档案站 · 事件梳理与来龙去脉
          </span>
          <h1 className="font-serif-editorial mt-6 text-4xl font-black leading-[1.2] text-foreground-950 md:text-5xl lg:text-6xl">
            一瓜到底，
            <br />
            把<span className="relative whitespace-nowrap text-primary-700">
              来龙去脉
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 9" fill="none" preserveAspectRatio="none">
                <path d="M2 6C60 2 140 2 198 6" stroke="currentColor" className="text-accent-500" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </span>讲清楚。
          </h1>
          <p className="mt-7 text-lg leading-8 text-foreground-600">
            天涯吃瓜专注<span className="font-semibold text-foreground-900">八卦汇总、事件梳理与来龙去脉全记录</span>。无论新瓜旧瓜，
            从起因、关键节点到后续进展，我们为你还原完整时间线，快速理清脉络、看懂背后门道。
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              onClick={() => goTo("hot")}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-7 py-3.5 text-[15px] font-medium text-white transition-colors hover:bg-primary-700"
            >
              <i className="ri-fire-line text-lg"></i>
              围观今日热瓜
            </button>
            <button
              onClick={() => goTo("archive")}
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-7 py-3.5 text-[15px] font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-time-line text-lg"></i>
              翻事件档案库
            </button>
          </div>

          {/* quick stats */}
          <dl className="mt-10 grid grid-cols-3 gap-4 border-t border-background-200 pt-7">
            {[
              { num: "1,286+", label: "梳理事件" },
              { num: "24,310+", label: "时间线节点" },
              { num: "8 年", label: "持续追踪" },
            ].map((s) => (
              <div key={s.label}>
                <dt className="sr-only">{s.label}</dt>
                <dd className="font-serif-editorial text-2xl font-black text-foreground-950 md:text-3xl">
                  {s.num}
                </dd>
                <dd className="mt-1 text-xs text-foreground-500 md:text-sm">{s.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* editorial collage */}
        <div className="relative hidden lg:block">
          <div className="relative ml-auto w-[420px]">
            <div className="overflow-hidden rounded-3xl border border-background-200">
              <img
                src={heroImages[0].img}
                alt={heroImages[0].alt}
                className="h-[460px] w-full object-cover"
              />
            </div>
            <div className="absolute -left-28 -bottom-10 w-64 overflow-hidden rounded-2xl border-4 border-background-50 shadow-xl">
              <img
                src={heroImages[1].img}
                alt={heroImages[1].alt}
                className="h-40 w-full object-cover"
              />
            </div>
            {/* floating tag card */}
            <div className="absolute -left-10 top-8 flex items-center gap-3 rounded-2xl border border-background-200 bg-background-50 px-4 py-3 shadow-lg">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 text-white">
                <i className="ri-megaphone-line"></i>
              </span>
              <div className="leading-tight">
                <p className="text-[11px] text-foreground-500">正在追踪</p>
                <p className="text-sm font-semibold text-foreground-900">沉浸式综艺口碑反转</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* category quick strip */}
      <div className="relative border-y border-background-200 bg-background-100">
        <div className="section-wrap flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-price-tag-3-line text-primary-600"></i>
            热门分类
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { i: "ri-vip-crown-line", t: "娱乐圈" },
              { i: "ri-fire-line", t: "网络热点" },
              { i: "ri-briefcase-line", t: "商业圈" },
              { i: "ri-earth-line", t: "社会议题" },
            ].map((c) => (
              <button
                key={c.t}
                onClick={() => goTo("archive")}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className={`${c.i} text-primary-600`}></i>
                {c.t}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}